/**
 * @file CoachsChisteraSpecialites.tsx
 * @summary Article de blog — CoachsChisteraSpecialites
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/coachs-crossfit-chistera-specialites";
const TITLE = "Les coachs CrossFit Chistera : équipe, parcours et spécialités";
const DESC = "Présentation de l'équipe coaching CrossFit Chistera : parcours, certifications, spécialités (compétition, retour de blessure, débutants, force). Ce qui fait notre approche.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Tous vos coachs sont-ils certifiés ?", a: "Oui. Tous sont au minimum CrossFit Level 1, et plusieurs ont des diplômes complémentaires (STAPS, BPJEPS, formations spécifiques HYROX, gymnastique, haltérophilie)." },
  { q: "Puis-je choisir mon coach ?", a: "Pour les classes de groupe, vous suivez le coach du créneau. Pour le coaching individuel, oui — on vous propose le coach dont les spécialités collent le mieux à votre objectif." },
  { q: "Vos coachs s'entraînent-ils eux-mêmes ?", a: "Tous, oui — c'est non négociable chez nous. La pratique régulière garde l'œil affûté sur la technique et sur ce que vivent réellement les athlètes." },
  { q: "Avez-vous un kiné ou un médecin partenaire ?", a: "Oui, nous travaillons avec un réseau de kinés et préparateurs physiques de la région, notamment pour les retours de blessure ou d'opération." },
  { q: "Et la formation continue ?", a: "Chaque coach a un budget formation annuel et participe à au moins un stage par an (CrossFit, gymnastique, force, mobilité, ou spécialité demandée)." },
];

export default function CoachsChisteraSpecialites() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Les coachs CrossFit Chistera et leurs spécialités"
      breadcrumbLabel="L'équipe coaching"
      category="Méthode · L'équipe"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-08" readingMinutes={7}
      ctaVariant="default"
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-08", author, reviewer, faq: faq.map(f => ({ q: f.q, a: typeof f.a === "string" ? f.a : "" })) })}
    >
      <EnBref>
        <p>Une box, ce sont d'abord <strong>des coachs</strong>. Chez Chistera, nous sommes une équipe complémentaire : compétiteurs aguerris, spécialistes du retour de blessure, pédagogues pour débutants. Toutes nos certifications sont à jour, tous les coachs s'entraînent eux-mêmes, et chacun se forme en continu.</p>
      </EnBref>

      <h2>Notre philosophie de coaching</h2>
      <p>Trois principes guident notre travail au quotidien :</p>
      <ol>
        <li><strong>La technique avant la charge.</strong> On ne fait pas progresser quelqu'un en lui faisant lever lourd avant qu'il ne maîtrise le mouvement. Cela paraît évident — c'est pourtant la première cause de blessure dans les box mal encadrées.</li>
        <li><strong>L'adaptation est la règle.</strong> Chaque WOD est adaptable. Un coach qui dit "tu fais comme c'est écrit ou tu ne fais pas" ne fait pas son métier. Voir notre approche détaillée dans <Link to="/blog/faire-du-sport-avec-douleur">"Faire du sport avec une douleur"</Link>.</li>
        <li><strong>L'athlète est acteur de sa progression.</strong> On explique les choix de programmation, on mesure les progrès, on ajuste avec vous — pas sur vous.</li>
      </ol>

      <h2>L'équipe et les spécialités</h2>

      <h3>Cédric Bastien & Anthony Bendahmane — Coach, co-fondateur</h3>
      <p>Co-fondateur de CrossFit Chistera. Spécialités : <strong>haltérophilie</strong>, <strong>programmation force</strong>, <strong>coaching individuel compétition</strong>. Plusieurs années d'encadrement de compétiteurs (CrossFit Open, ATHX, HYROX). Approche méthodique et progressive.</p>

      <h3>Anthony Bendahmane (Benbou) — Coach</h3>
      <p>Master STAPS et CrossFit Level 1. Spécialités : <strong>retour de blessure</strong>, <strong>réathlétisation</strong>, <strong>sport et pathologies</strong>. Travaille en lien avec les kinés du réseau pour les protocoles de reprise. Référent sur les contenus santé du blog.</p>

      <h3>Le reste de l'équipe</h3>
      <p>L'équipe complète tourne sur les créneaux WOD, le programme <Link to="/cours/hybrid-training">Hybrid Training</Link>, le programme <Link to="/crossfit-debutant">débutant</Link> et le coaching individuel. Vous croiserez chacun selon les créneaux — n'hésitez pas à demander leur parcours et leurs spécialités.</p>

      <h2>Pourquoi ça compte vraiment</h2>
      <p>Une box vit grâce à ses coachs. Le matériel, l'horaire, le tarif comptent — mais c'est l'œil du coach qui transforme un WOD générique en séance qui vous fait progresser <strong>sans vous casser</strong>. C'est ce que nous voulons offrir, jour après jour.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Avis CrossFit Chistera (retours d'adhérents)", to: "/blog/avis-crossfit-chistera" },
        { label: "Coaching individuel selon vos objectifs", to: "/blog/coaching-individuel-objectifs-sportifs" },
        { label: "Reprendre le sport après une blessure", to: "/blog/reprendre-le-sport-apres-blessure" },
      ]} />

      <SourcesSection items={[
        { label: "CrossFit — Find a Trainer (vérification des certifications)", href: "https://www.crossfit.com/trainer-directory" },
      ]} />
    </ArticleLayout>
  );
}
